import type { UIApiConfig } from '@sydle/ui-api';
import type { CreateDialogOptions } from '@sydle/ui-design-system';
import { dialogController } from '@sydle/ui-design-system';
import { getConfig } from '@sydle/ui-utils';
import { syQuerySelector } from './dom.utils';
import { setLoading } from './loading.utils';

export class DialogHelper {
  private static dialogs: { key: string; dialog: HTMLSyDialogElement }[] = [];
  private static providerElem: HTMLSyContextProviderElement;

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static async open<T>(options: CreateDialogOptions<T>) {
    await this.createPortalProvider();

    options.hideFooter = true;

    const newDialog = dialogController.create(options);
    this.providerElem?.appendChild(newDialog.element);

    // Push dialog reference to the stack using the content component tag as id.
    const elementName =
      typeof options.content.slotComponent === 'string'
        ? options.content.slotComponent
        : options?.content?.slotComponent?.tagName.toLowerCase();
    this.dialogs.push({ key: elementName as string, dialog: newDialog.element });

    document.body.style.overflow = 'hidden';
    newDialog.element.style.cssText = newDialog.element.style.cssText.concat('--sy-dismiss-icon-size-large: 24px');

    setLoading(true);
    newDialog.open().finally(() => {
      setLoading(false);

      const dialogElement = syQuerySelector('sy-dialog', document.body) as HTMLElement;
      const messageButtonElement = syQuerySelector('sy-one-message-button', dialogElement);

      if (messageButtonElement) {
        (syQuerySelector('button', messageButtonElement) as HTMLElement)?.focus();
      } else {
        const dismissButton = syQuerySelector('sy-dismiss', dialogElement);

        if (dismissButton) {
          (syQuerySelector('button', dismissButton) as HTMLElement)?.focus();
        }
      }
    });

    newDialog.onClose().then(() => {
      document.body.style.overflow = 'visible';
      this.destroy(newDialog.element);
    });

    return newDialog;
  }

  static async close(): Promise<void> {
    if (this.dialogs?.length > 0) {
      return await this.dialogs[this.dialogs.length - 1].dialog.close();
    }
  }

  private static destroy(dialog: HTMLSyDialogElement): void {
    const dialogIndex = this.dialogs.findIndex((item) => item.dialog === dialog);
    if (dialogIndex >= 0) {
      dialog.remove();
      this.dialogs.splice(dialogIndex, 1);
    }
  }

  static init(): void {
    if (this.providerElem) {
      this.providerElem.remove();
    }

    this.createPortalProvider();
  }

  static setTitle(dialogRef: HTMLSyDialogElement, title: string): void {
    const dialogHeader = syQuerySelector('sy-dialog-header', dialogRef) as HTMLSyDialogHeaderElement;

    if (dialogHeader) {
      dialogHeader.dialogTitle = title;
    }
  }

  private static async createPortalProvider(): Promise<void> {
    if (this.providerElem?.isConnected && this.providerElem?.context === 'portal') return;

    const providerElem = document.createElement('sy-context-provider') as HTMLSyContextProviderElement;
    providerElem.id = 'sy-sd-dialog-provider';
    providerElem.context = 'portal';
    providerElem.value = getConfig<UIApiConfig>('ui-app')?.ORGANIZATION_IDENTIFIER;
    providerElem.setAttribute('data-sy-viewport', '');
    document.body.appendChild(providerElem);

    this.providerElem = providerElem;
  }
}
